/**
 * useSpeedWarning
 * -----------------------------------------------------------------
 * Driver-side heads-up BEFORE the backend's 120 km/h public safety
 * auto-alert kicks in. When the live device speed crosses 110 km/h,
 * we surface a full-screen warning modal + a short beep so the driver
 * can slow down and avoid being publicly named on the Community feed.
 *
 * Also reports the event to the backend so admins can compute a
 * per-driver "safety score" (compliance rate = slowed_down / total).
 *
 * State:
 *   - active: boolean — is the warning currently displayed?
 *   - peakKmh: number — the highest speed seen during this warning cycle
 *
 * Behaviour:
 *   - Warning stays open until the driver dismisses OR speed drops
 *     below 100 km/h for at least 5 seconds (hysteresis so a brief dip
 *     below the trigger doesn't flap the modal).
 *   - After dismissal, a 60-second cooldown prevents immediate re-warn
 *     even if the driver stays hot on the throttle.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const WARN_KMH = 110;
const CLEAR_KMH = 100;
const CLEAR_HOLD_MS = 5000;
const DISMISS_COOLDOWN_MS = 60_000;

export function useSpeedWarning(speedKmh) {
  const [active, setActive] = useState(false);
  const [peakKmh, setPeakKmh] = useState(0);
  const dismissedUntilRef = useRef(0);
  const belowSinceRef = useRef(null);
  const audioCtxRef = useRef(null);
  // Backend telemetry event id — one per warning cycle. We stash the peak
  // km/h too so a burst of `slowed-down` calls after auto-clear can safely
  // no-op if the id already resolved server-side (e.g. escalated to 120+).
  const eventIdRef = useRef(null);
  const slowedReportedRef = useRef(false);

  // Small chirp using Web Audio API (no assets required). Reused
  // instrument context is created lazily on first warning so browsers
  // don't complain about audio-without-user-gesture on cold load.
  const beep = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      const now = ctx.currentTime;
      // Two-tone urgent chirp (700Hz → 950Hz).
      [700, 950].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = freq;
        osc.type = "square";
        gain.gain.setValueAtTime(0.0001, now + i * 0.22);
        gain.gain.exponentialRampToValueAtTime(0.25, now + i * 0.22 + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.22 + 0.18);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + i * 0.22);
        osc.stop(now + i * 0.22 + 0.2);
      });
    } catch {
      /* audio unavailable — modal still shows */
    }
  }, []);

  // Report the pre-warning to the backend so admin has telemetry. Fire-
  // and-forget: the modal must never block on a network call.
  const reportEvent = useCallback(async (peak) => {
    try {
      const { data } = await api.post("/drivers/speed-events", { peak_kmh: peak });
      eventIdRef.current = data?.event_id || null;
      slowedReportedRef.current = false;
    } catch {
      /* offline / not-a-driver / etc — ignore, modal still works */
    }
  }, []);

  const reportSlowedDown = useCallback(async () => {
    const id = eventIdRef.current;
    if (!id || slowedReportedRef.current) return;
    slowedReportedRef.current = true;
    try {
      await api.post(`/drivers/speed-events/${id}/slowed-down`);
    } catch {
      /* ignore — backend may have already escalated to `escalated` */
    }
  }, []);

  useEffect(() => {
    if (typeof speedKmh !== "number" || Number.isNaN(speedKmh)) return;

    // Track peak while active.
    if (active) {
      setPeakKmh((p) => (speedKmh > p ? speedKmh : p));
    }

    if (speedKmh >= WARN_KMH) {
      belowSinceRef.current = null;
      const now = Date.now();
      if (!active && now >= dismissedUntilRef.current) {
        setActive(true);
        setPeakKmh(speedKmh);
        beep();
        try { navigator.vibrate?.([200, 100, 200, 100, 200]); } catch { /* no-op */ }
        reportEvent(speedKmh);
      }
      return;
    }

    // Speed below warning threshold — auto-clear only after sustained
    // hysteresis window (prevents flapping around 110 km/h).
    if (active && speedKmh < CLEAR_KMH) {
      if (belowSinceRef.current == null) belowSinceRef.current = Date.now();
      else if (Date.now() - belowSinceRef.current >= CLEAR_HOLD_MS) {
        setActive(false);
        belowSinceRef.current = null;
        reportSlowedDown();
      }
    }
  }, [speedKmh, active, beep, reportEvent, reportSlowedDown]);

  const dismiss = useCallback(() => {
    setActive(false);
    dismissedUntilRef.current = Date.now() + DISMISS_COOLDOWN_MS;
    belowSinceRef.current = null;
    // If the driver taps "I'll slow down" while already under 100 km/h,
    // credit them immediately. Otherwise the hysteresis path handles it.
    if (typeof speedKmh === "number" && speedKmh < CLEAR_KMH) {
      reportSlowedDown();
    }
  }, [speedKmh, reportSlowedDown]);

  return { active, peakKmh, dismiss, warnThreshold: WARN_KMH };
}
